import { useEffect, useState } from "react";
import { Button } from "@chakra-ui/react";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { TimeZonePickerWithLabel } from "newui/components/ui/InputWithLabel/TimeZonePickerWithLabel";
import { showToast } from "utils/helpers";
import { notificationsLang } from "../lang/en";
import { useSaveDigestSettings } from "../hooks/useSavePreferences";
import type { DigestSettings } from "../types/Notifications.type";

export interface DigestTimingCardProps {
	initial?: DigestSettings;
}

const SEND_HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
	label: `${String(hour).padStart(2, "0")}:00`,
	value: hour,
}));

export const DigestTimingCard = ({ initial }: DigestTimingCardProps) => {
	const [sendHour, setSendHour] = useState<number>(initial?.digest_send_hour ?? 8);
	const [timezone, setTimezone] = useState<string>(initial?.timezone ?? "UTC");
	const saveMutation = useSaveDigestSettings();

	useEffect(() => {
		if (initial) {
			setSendHour(initial.digest_send_hour);
			setTimezone(initial.timezone);
		}
	}, [initial]);

	const handleSave = () => {
		saveMutation.mutate(
			{ digest_send_hour: sendHour, timezone },
			{
				onSuccess: () => {
					showToast(
						notificationsLang.saveSuccessDescription,
						notificationsLang.saveSuccessTitle,
						true,
						"notif-digest-saved"
					);
				},
				onError: () => {
					showToast(
						notificationsLang.saveErrorDescription,
						notificationsLang.saveErrorTitle,
						false,
						"notif-digest-error"
					);
				},
			}
		);
	};

	return (
		<div className="mt-6 rounded-lg border border-gray-200 p-4" data-testid="digest-timing-card">
			<h3 className="text-sm font-semibold text-gray-900">{notificationsLang.digestCardHeading}</h3>
			<p className="mt-1 text-xs text-gray-500">{notificationsLang.digestCardDescription}</p>

			<div className="mt-4 flex flex-wrap items-end gap-4">
				<SelectWithLabel
					name="digest-send-hour"
					label={notificationsLang.digestSendHourLabel}
					testId="digest-send-hour-select"
					options={SEND_HOUR_OPTIONS}
					value={sendHour}
					width="140px"
					onChange={(option) => {
						if (option && !Array.isArray(option)) {
							setSendHour(option.value as number);
						}
					}}
				/>

				<TimeZonePickerWithLabel
					name="digest-timezone"
					label={notificationsLang.digestTimezoneLabel}
					testId="digest-timezone-select"
					value={timezone}
					onChange={(tz) => setTimezone(tz)}
				/>

				<Button
					size="sm"
					colorScheme="blue"
					onClick={handleSave}
					isLoading={saveMutation.isPending}
					data-testid="digest-save-button"
				>
					{notificationsLang.digestSaveButton}
				</Button>
			</div>
		</div>
	);
};
